import { cursorImage } from '../presentation/cursors.js';

const INTERACTIVE = 'button, a, input, select, textarea, summary, [role="button"]';

/** Translate pointer-lock deltas into ordinary game/UI coordinates. */
export class ConfinedPointer {
  private listeners = new AbortController();
  private cursor: HTMLDivElement;
  private x = 0;
  private y = 0;
  private buttons = 0;
  private pressed: Element | null = null;
  private pressX = 0;
  private pressY = 0;
  private forwarding = false;
  private document: Document;

  constructor(private root: HTMLElement) {
    this.document = root.ownerDocument;
    this.cursor = this.document.createElement('div');
    this.cursor.className = 'game-cursor'; this.cursor.hidden = true;
    this.cursor.setAttribute('aria-hidden', 'true'); root.append(this.cursor);
    const options = { capture: true, signal: this.listeners.signal };
    for (const type of ['pointerdown', 'pointermove', 'pointerup', 'pointercancel', 'click', 'auxclick', 'contextmenu']) {
      this.document.addEventListener(type, event => {
        if (this.locked && !this.forwarding) this.block(event);
      }, options);
    }
    this.document.addEventListener('mousemove', event => {
      if (!this.locked || this.forwarding) return;
      this.block(event); this.move(event);
    }, options);
    this.document.addEventListener('mousedown', event => {
      if (!this.locked || this.forwarding) return;
      this.block(event); this.down(event);
    }, options);
    this.document.addEventListener('mouseup', event => {
      if (!this.locked || this.forwarding) return;
      this.block(event); this.up(event);
    }, options);
    this.document.addEventListener('wheel', event => {
      if (!this.locked || this.forwarding) return;
      this.block(event);
      const target = this.target(); if (!target) return;
      if (target.closest('#stage')) {
        this.forward(() => target.dispatchEvent(new WheelEvent('wheel', {
          bubbles: true, cancelable: true, clientX: this.x, clientY: this.y,
          deltaX: event.deltaX, deltaY: event.deltaY, deltaZ: event.deltaZ, deltaMode: event.deltaMode,
        })));
      } else {
        // Synthetic wheel events have no native scrolling default action.
        let scrollable: Element | null = target;
        while (scrollable && scrollable !== this.root) {
          if (scrollable.scrollHeight > scrollable.clientHeight) {
            const scale = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? scrollable.clientHeight : 1;
            scrollable.scrollTop += event.deltaY * scale; break;
          }
          scrollable = scrollable.parentElement;
        }
      }
    }, { ...options, passive: false });
    this.document.addEventListener('pointerlockchange', () => this.changed(), { signal: options.signal });
    this.document.defaultView!.addEventListener('resize', () => { if (this.locked) this.draw(); }, { signal: options.signal });
    this.document.defaultView!.addEventListener('blur', () => this.release(), { signal: options.signal });
  }

  get locked() { return this.document.pointerLockElement === this.root; }

  release() { if (this.locked) this.document.exitPointerLock(); }

  dispose() {
    this.release(); this.cancel(); this.listeners.abort();
    this.root.classList.remove('cursor-confined'); this.cursor.remove();
  }

  private changed() {
    this.cancel(); this.cursor.hidden = !this.locked;
    this.root.classList.toggle('cursor-confined', this.locked);
    if (this.locked) {
      const rect = this.root.getBoundingClientRect();
      this.x = rect.left + rect.width / 2; this.y = rect.top + rect.height / 2; this.draw();
      this.pointer('pointermove', this.target(), new MouseEvent('mousemove'));
    }
  }

  private move(event: MouseEvent) {
    this.x += event.movementX; this.y += event.movementY; this.buttons = event.buttons;
    // Clamp first, then let the page react, then draw: the cursor the world chose for this spot is the one shown.
    this.clamp(); this.pointer('pointermove', this.pressed ?? this.target(), event); this.draw();
  }

  private down(event: MouseEvent) {
    this.buttons = event.buttons;
    if (!this.pressed) { this.pressX = this.x; this.pressY = this.y; }
    this.pressed ??= this.target();
    const target = this.pressed;
    this.pointer('pointerdown', target, event);
    if (event.button === 0) (target?.closest(INTERACTIVE) as HTMLElement | null)?.focus({ preventScroll: true });
  }

  private up(event: MouseEvent) {
    this.buttons = event.buttons;
    const pressed = this.pressed, target = this.target();
    this.pointer('pointerup', pressed ?? target, event);
    if (!event.buttons) this.pressed = null;
    const control = pressed?.closest(INTERACTIVE) as HTMLElement | null;
    if (control && 'disabled' in control && control.disabled) return;
    if (event.button !== 0 || !pressed || Math.hypot(this.x - this.pressX, this.y - this.pressY) > 6) return;
    if (control ? target?.closest(INTERACTIVE) !== control : target !== pressed) return;
    // Native text editing and select menus need the real cursor and browser defaults.
    if (control?.matches('select, textarea, input:not([type="checkbox"]):not([type="radio"])')) {
      this.release(); control.focus({ preventScroll: true });
      if (control instanceof HTMLSelectElement) {
        try { control.showPicker(); } catch { /* Keyboard selection remains available. */ }
      }
      return;
    }
    this.forward(() => (control ?? pressed).dispatchEvent(new MouseEvent('click', {
      bubbles: true, cancelable: true, clientX: this.x, clientY: this.y,
      shiftKey: event.shiftKey, ctrlKey: event.ctrlKey, altKey: event.altKey, metaKey: event.metaKey,
    })));
  }

  private target() { return this.document.elementFromPoint(this.x, this.y); }

  private clamp() {
    const rect = this.root.getBoundingClientRect();
    this.x = Math.max(rect.left, Math.min(rect.right - 1, this.x));
    this.y = Math.max(rect.top, Math.min(rect.bottom - 1, this.y));
  }

  private draw() {
    this.clamp();
    // The browser draws no cursor while locked, so this one shows the baked cursor the element under it asks for.
    const target = this.target(), style = target && this.document.defaultView?.getComputedStyle?.(target), image = style ? cursorImage(style.cursor) : undefined;
    const [x, y] = image?.hotspot ?? [0, 0];
    this.cursor.style.transform = `translate(${this.x - x}px, ${this.y - y}px)`;
    if (image) this.cursor.style.setProperty('--image', `image-set(url("${image.url}") 1x, url("${image.large}") 2x)`);
    this.cursor.classList.toggle('baked', !!image);
    this.cursor.classList.toggle('interactive', !!target?.closest(INTERACTIVE));
  }

  private pointer(type: string, target: Element | null, event: MouseEvent) {
    if (!target) return;
    this.forward(() => target.dispatchEvent(new PointerEvent(type, {
      bubbles: true, cancelable: true, pointerId: 1, pointerType: 'mouse', isPrimary: true,
      clientX: this.x, clientY: this.y, button: event.button, buttons: this.buttons,
      movementX: event.movementX, movementY: event.movementY,
      shiftKey: event.shiftKey, ctrlKey: event.ctrlKey, altKey: event.altKey, metaKey: event.metaKey,
    })));
  }

  private cancel() {
    this.pointer('pointercancel', this.pressed, new MouseEvent('mouseup'));
    this.pressed = null; this.buttons = 0;
    this.forward(() => this.document.dispatchEvent(new Event('pointerleave')));
  }

  private forward(action: () => unknown) {
    this.forwarding = true;
    try { action(); } finally { this.forwarding = false; }
  }

  private block(event: Event) { event.preventDefault(); event.stopImmediatePropagation(); }
}
